import type { IExtendedSession } from "./types/next-auth";

type GetSessionOptions = Pick<RequestInit, "cache" | "credentials" | "headers">;

const getPublicAuthUrl = () =>
  process.env.NEXT_PUBLIC_AUTH_URL ?? process.env.REACT_APP_AUTH_URL;

const getAuthUrl = () => {
  const publicAuthUrl = getPublicAuthUrl();

  if (typeof window !== "undefined") {
    return publicAuthUrl ?? null;
  }

  const serverAuthUrl =
    process.env.AUTH_ORIGIN_URL ??
    process.env.AUTH_INTERNAL_URL ??
    publicAuthUrl;

  return serverAuthUrl ?? null;
};

const getSessionHeaders = (headers?: HeadersInit) => {
  const sessionHeaders = new Headers(headers);

  if (typeof window !== "undefined") {
    return sessionHeaders;
  }

  const publicAuthUrl = getPublicAuthUrl();
  const serverAuthUrl = getAuthUrl();

  if (!publicAuthUrl || !serverAuthUrl) {
    return sessionHeaders;
  }

  if (publicAuthUrl === serverAuthUrl) {
    return sessionHeaders;
  }

  const publicUrl = new URL(publicAuthUrl);

  if (!sessionHeaders.has("X-Forwarded-Host")) {
    sessionHeaders.set("X-Forwarded-Host", publicUrl.host);
  }

  if (!sessionHeaders.has("X-Forwarded-Proto")) {
    sessionHeaders.set(
      "X-Forwarded-Proto",
      publicUrl.protocol.replace(":", ""),
    );
  }

  return sessionHeaders;
};

/**
 * Fetches the current user session from the auth app via `/api/auth/session`.
 *
 * @param options - Optional fetch configuration for `headers` and `credentials`.
 * @returns The session data, or `null` if the request fails or returns a non-OK status.
 *
 * @example
 * // Server-side: forward the incoming cookie header
 * const session = await getSession({ headers: { Cookie: (await cookies()).toString() } });
 *
 * @example
 * // Client-side: include credentials automatically
 * const session = await getSession({ credentials: 'include' });
 */
export const getSession = async (
  options?: GetSessionOptions,
): Promise<IExtendedSession | null> => {
  try {
    const authUrl = getAuthUrl();

    if (!authUrl) {
      console.error("Error getting session: missing auth URL");
      return null;
    }

    const res = await fetch(`${authUrl}/api/auth/session`, {
      cache: "no-store",
      ...options,
      headers: getSessionHeaders(options?.headers),
    });

    if (!res.ok) {
      console.error("Error getting session:", res.statusText);
      return null;
    }

    const response: IExtendedSession | null = await res.json();
    return response;
  } catch (error) {
    console.error("Error getting session:", error);
    return null;
  }
};
